import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Form, Alert, Badge, Modal } from 'react-bootstrap';
import { motion, AnimatePresence } from 'framer-motion';
import { Search as FaSearch, Send as FaPaperPlane, UserCheck as FaUserCheck, Clock as FaUserClock, UserX as FaUserTimes, Unlink as FaUnlink } from 'lucide-react';
import api from '../../api/axios';
import Loader from '../../components/Loader';
import GradientButton from '../../components/GradientButton';

const STATUS_META = {
  pending: { label: 'Oczekuje', color: '#f59f00', bg: '#fff9db', icon: FaUserClock },
  accepted: { label: 'Połączony', color: '#2b8a3e', bg: '#e8f5e9', icon: FaUserCheck },
  rejected: { label: 'Odrzucone', color: '#e03131', bg: '#fff5f5', icon: FaUserTimes },
};

function EmployerLinkRequests() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [showUnlinkModal, setShowUnlinkModal] = useState(false);
  const [linkToUnlink, setLinkToUnlink] = useState(null);

  const [linkedWorkers, setLinkedWorkers] = useState([]);
  const [loadingLinks, setLoadingLinks] = useState(true);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const fadeInUp = {
    initial: { opacity: 0, y: 40 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.5 }
  };

  const fetchLinkedWorkers = useCallback(async () => {
    setLoadingLinks(true);
    try {
      const res = await api.get('/employer/linked-workers/');
      setLinkedWorkers(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingLinks(false);
    }
  }, []);

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

  useEffect(() => {
    if (!isValidEmail(query)) {
      setResults([]);
      setSearchError('');
      return;
    }

    setSearching(true);
    setSearchError('');

    const timeoutId = setTimeout(async () => {
      try {
        const res = await api.get(`/employer/search-workers/?q=${encodeURIComponent(query.trim())}`);
        setResults(res.data);
      } catch (err) {
        setSearchError(err.response?.data?.detail || 'Nie udało się wyszukać pracownika.');
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [query]);

  useEffect(() => { fetchLinkedWorkers(); }, [fetchLinkedWorkers]);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (query.trim().length < 3) {
      setSearchError('Wpisz co najmniej 3 znaki.');
      return;
    }
    setSearching(true);
    setSearchError('');
    try {
      const res = await api.get(`/employer/search-workers/?q=${encodeURIComponent(query.trim())}`);
      setResults(res.data);
    } catch (err) {
      setSearchError(err.response?.data?.detail || 'Nie udało się wyszukać pracowników.');
    } finally {
      setSearching(false);
    }
  };

  const handleSendRequest = async (workerId) => {
    setError('');
    try {
      await api.post(`/employer/link-requests/${workerId}/`);
      setSuccess('Zaproszenie zostało wysłane');
      setResults(prev => prev.map(u => u.id === workerId ? { ...u, link_status: 'pending' } : u));
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Nie udało się wysłać zaproszenia');
    }
  };

  const handleUnlinkClick = (link) => {
    setLinkToUnlink(link);
    setShowUnlinkModal(true);
  };

  const handleConfirmUnlink = async () => {
    if (!linkToUnlink) return;
    setError('');
    try {
      await api.post(`/vacation/links/${linkToUnlink.id}/unlink/`);
      setSuccess('Połączenie zostało zakończone');
      fetchLinkedWorkers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Nie udało się zakończyć połączenia');
    }
    setShowUnlinkModal(false);
    setLinkToUnlink(null);
  };

  return (
    <div style={{ minHeight: '100vh', paddingTop: '2rem', paddingBottom: '4rem' }}>
      <Container fluid className="px-2 px-md-4">

        <motion.div {...fadeInUp} className="mb-4">
          <h1 className="pub-h2 mb-1">Połącz konto pracownika</h1>
          <p className="pub-muted mb-0">Wyszukaj pracownika po nazwie użytkownika lub emailu i wyślij zaproszenie</p>
        </motion.div>

        {success && <Alert variant="success" className="border-0" style={{ borderRadius: '15px' }}>{success}</Alert>}
        {error && <Alert variant="danger" className="border-0" style={{ borderRadius: '15px' }}>{error}</Alert>}

        <Row className="g-4">
          {/* WYSZUKIWARKA */}
          <Col xs={12} lg={6}>
            <motion.div {...fadeInUp}>
              <Card className="border-0 shadow-lg" style={{ borderRadius: '20px', overflow: 'hidden' }}>
                <Card.Header style={{ padding: '1.25rem' }}>
                  <h6 className="mb-0 fw-bold">Wyszukaj pracownika</h6>
                </Card.Header>
                <Card.Body className="p-3">
                  <div className="d-flex gap-2 mb-3">
                    <Form.Control
                      type="text"
                      placeholder="Wpisz pełny adres email pracownika..."
                      value={query}
                      onChange={e => setQuery(e.target.value)}
                      style={{ borderRadius: '12px', color: '#212529' }}
                    />
                    <div
                      className="d-flex align-items-center justify-content-center"
                      style={{ width: '42px', flexShrink: 0 }}
                    >
                      {searching ? (
                        <span className="spinner-border spinner-border-sm text-primary" role="status" />
                      ) : (
                        <FaSearch color="#868e96" />
                      )}
                    </div>
                  </div>

                  {searchError && (
                    <Alert variant="warning" className="border-0" style={{ borderRadius: '12px' }}>{searchError}</Alert>
                  )}

                  {searchError && (
                    <Alert variant="warning" className="border-0" style={{ borderRadius: '12px' }}>{searchError}</Alert>
                  )}

                  <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    {results.map(user => (
                      <div
                        key={user.id}
                        className="d-flex align-items-center justify-content-between p-3 mb-2"
                        style={{ borderRadius: '14px', background: '#f8f9fa' }}
                      >
                        <div>
                          <div style={{ color: '#212529', fontWeight: 600, fontSize: '14px' }}>{user.name}</div>
                          <div style={{ color: '#868e96', fontSize: '12px' }}>{user.email}</div>
                        </div>
                        {user.link_status === 'pending' ? (
                          <Badge style={{ background: '#f59f00' }}>Oczekuje</Badge>
                        ) : user.link_status === 'accepted' ? (
                          <Badge style={{ background: '#2b8a3e' }}>Połączony</Badge>
                        ) : (
                          <button
                            className="btn btn-sm btn-primary d-flex align-items-center gap-1"
                            style={{ borderRadius: '10px' }}
                            onClick={() => handleSendRequest(user.id)}
                          >
                            <FaPaperPlane size={11} /> Zaproś
                          </button>
                        )}
                      </div>
                    ))}
                    {!searching && results.length === 0 && isValidEmail(query) && (
                      <p className="text-center text-muted py-3 mb-0">Nie znaleziono pracownika z tym adresem email.</p>
                    )}
                    {!isValidEmail(query) && query.length > 0 && (
                      <p className="text-center text-muted py-3 mb-0">Wpisz pełny, poprawny adres email.</p>
                    )}
                  </div>
                </Card.Body>
              </Card>
            </motion.div>
          </Col>

          {/* POŁĄCZENI / OCZEKUJĄCY */}
          <Col xs={12} lg={6}>
            <motion.div {...fadeInUp}>
              <Card className="border-0 shadow-lg" style={{ borderRadius: '20px', overflow: 'hidden' }}>
                <Card.Header style={{ background: '#f8f9fa', border: 'none', padding: '1.25rem' }}>
                  <h6 className="mb-0 fw-bold" style={{ color: '#212529' }}>Zaproszenia i połączenia</h6>
                </Card.Header>
                <Card.Body className="p-3">
                  {loadingLinks ? (
                    <Loader />
                  ) : linkedWorkers.length === 0 ? (
                    <p className="text-center text-muted py-4 mb-0">Brak zaproszeń ani połączonych kont.</p>
                  ) : (
                    <AnimatePresence>
                      {linkedWorkers.map(link => {
                        const meta = STATUS_META[link.status];
                        const Icon = meta.icon;
                        return (
                          <motion.div
                            key={link.id}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 10 }}
                            className="d-flex align-items-center justify-content-between p-3 mb-2"
                            style={{ borderRadius: '14px', background: meta.bg, border: `1px solid ${meta.color}33` }}
                          >
                            <div className="d-flex align-items-center gap-2">
                              <Icon color={meta.color} />
                              <div>
                                <div style={{ color: '#212529', fontWeight: 600, fontSize: '14px' }}>{link.worker_name} {link.worker_last_name}</div>
                                <div style={{ color: '#868e96', fontSize: '12px' }}>{link.worker_email}</div>
                              </div>
                            </div>
                            {link.status === 'accepted' ? (
                              <button
                                className="btn btn-sm btn-outline-danger d-flex align-items-center gap-1"
                                style={{ borderRadius: '10px' }}
                                onClick={() => handleUnlinkClick(link)}
                              >
                                <FaUnlink size={11} /> Rozłącz
                              </button>
                            ) : (
                              <Badge style={{ background: meta.color }}>{meta.label}</Badge>
                            )}
                          </motion.div>
                        );
                      })}
                    </AnimatePresence>
                  )}
                </Card.Body>
              </Card>
            </motion.div>
          </Col>
</Row>

        {/* MODAL POTWIERDZENIA ROZŁĄCZENIA */}
        <Modal show={showUnlinkModal} onHide={() => setShowUnlinkModal(false)} centered>
          <Modal.Header closeButton style={{ border: 'none' }}>
            <Modal.Title>Potwierdzenie rozłączenia</Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            <p>
              Czy na pewno chcesz zakończyć połączenie z pracownikiem{' '}
              <strong>{linkToUnlink?.worker_name}</strong>?
            </p>
            <p className="text-danger small fw-medium mb-0">
              Pracownik straci możliwość składania wniosków urlopowych do Ciebie — będzie mógł połączyć się ponownie, jeśli wyślesz nowe zaproszenie.
            </p>
          </Modal.Body>
          <Modal.Footer style={{ border: 'none' }}>
            <button className="btn btn-light" onClick={() => setShowUnlinkModal(false)} style={{ borderRadius: '12px' }}>
              Anuluj
            </button>
            <button className="btn btn-danger" onClick={handleConfirmUnlink} style={{ borderRadius: '12px' }}>
              Rozłącz
            </button>
          </Modal.Footer>
        </Modal>
      </Container>
    </div>
  );
}

export default EmployerLinkRequests;